import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import {
  AppearanceContext,
  DEFAULT_APPEARANCE,
  apply,
  remember,
} from "./appearanceContext";
import type { AppearanceSettings } from "./types";

export function AppearanceProvider({ children }: { children: React.ReactNode }) {
  const [appearance, setAppearance] = useState<AppearanceSettings>(DEFAULT_APPEARANCE);

  useEffect(() => {
    // A failed load leaves the defaults applied — a readable app beats none.
    api
      .appearance()
      .then((loaded) => {
        setAppearance(loaded);
        apply(loaded);
        remember(loaded);
      })
      .catch(() => {});
  }, []);

  // Following the OS means following it as it changes, not only at load.
  useEffect(() => {
    if (appearance.theme !== "system") return;
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const sync = () => apply(appearance);
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, [appearance]);

  const save = useCallback(async (next: AppearanceSettings) => {
    // Applied before the round trip: a theme switch that waits on the network
    // feels broken even when it works.
    apply(next);
    remember(next);
    setAppearance(next);
    try {
      setAppearance(await api.saveAppearance(next));
    } catch (error) {
      apply(appearance);
      remember(appearance);
      setAppearance(appearance);
      throw error;
    }
  }, [appearance]);

  return <AppearanceContext.Provider value={{ appearance, save }}>{children}</AppearanceContext.Provider>;
}

